import apiV2 from '../../api/v2';
import { fetchLatestEventByType } from './timeline';

/**
 * Mirror of `DxReportOrthopulseStatus` on the files-viewer side. Kept as a
 * narrow local copy so the dashboard doesn't pull the whole Dx report
 * types module from the admin package.
 */
export type DxReportOrthopulseStatus = 'mandatory' | 'recommended' | 'not_needed';

export interface DxReportVersion {
    /** Timeline event id. */
    id: string;
    /** Monotonically increasing version number (1, 2, 3...). */
    version: number;
    /** ISO timestamp the version was approved/created. */
    createdAt: string;
    /** GCS-stored PDF file id; resolved to a download URL on demand. */
    patientFileId: string;
    /** Filename for display (e.g. "Dx-Wis_Robby-2026-05-12-v2.pdf"). */
    fileName: string;
}

interface RawTimelineEvent {
    id: string;
    version: number;
    status: string;
    createdAt: string;
    payload?: Record<string, unknown>;
}

function buildVersionFilename(
    payload: Record<string, unknown> | undefined,
    version: number
): string {
    // Reports imported off Monday carry no formData to synthesise a name from, so
    // they record the real filename on the file ref. Prefer it when present.
    const files = payload?.files as Record<string, unknown> | undefined;
    const dxPdf = files?.dxReportPdf as Record<string, unknown> | undefined;
    const imported = dxPdf?.originalFilename;
    if (typeof imported === 'string' && imported.trim().length > 0) return imported;

    const formData = payload?.formData as Record<string, unknown> | undefined;
    const patientInfo = formData?.patientInfo as Record<string, unknown> | undefined;
    const name = (patientInfo?.patientName as string) ?? '';
    const date = (patientInfo?.reportDate as string) ?? '';
    const sanitized = name
        .trim()
        .replace(/\s+/g, '_')
        .replace(/[^\w.-]/g, '');
    const versionSuffix = version > 1 ? `-v${version}` : '';
    return `Dx-${sanitized}-${date}${versionSuffix}.pdf`;
}

function extractPatientFileId(payload: Record<string, unknown> | undefined): string | null {
    const files = payload?.files as Record<string, unknown> | undefined;
    const dxPdf = files?.dxReportPdf as Record<string, unknown> | undefined;
    const id = dxPdf?.patientFileId;
    return typeof id === 'string' ? id : null;
}

/**
 * List all approved Dx report versions for a patient. Returns latest-first
 * (highest version number) so callers can default-select the head of the list.
 *
 * Omits `isLatest` so the backend skips the head-row filter and returns the
 * full version history across the group(s). Excludes drafts and invalidated
 * rows: only `status='active'` events are surfaced. Skips events whose
 * payload lacks a `patientFileId` (legacy / malformed rows can't be
 * displayed in the PDF viewer anyway).
 */
export async function fetchDxReportVersions(patientId: string): Promise<DxReportVersion[]> {
    const response = await apiV2.get<{ data: RawTimelineEvent[] }>(
        `/patients/${patientId}/timeline`,
        { params: { type: 'dx_report_submission', limit: 50 } }
    );
    const events = response.data?.data ?? [];
    return (
        events
            .filter((e) => e.status === 'active')
            .map((e) => {
                const patientFileId = extractPatientFileId(e.payload);
                if (!patientFileId) return null;
                return {
                    id: e.id,
                    version: e.version,
                    createdAt: e.createdAt,
                    patientFileId,
                    fileName: buildVersionFilename(e.payload, e.version)
                };
            })
            .filter((v): v is DxReportVersion => v !== null)
            // Reports imported off Monday all carry version 0, so the version number
            // alone cannot order them — fall back to the date they were filed.
            .sort((a, b) => b.version - a.version || b.createdAt.localeCompare(a.createdAt))
    );
}

/**
 * Read the orthopulse status the ortho team set on the latest Dx report
 * submission. Returns null when:
 *   - the patient has no `dx_report_submission` timeline event yet
 *   - the latest event predates the field being added (older drafts)
 *   - the payload is malformed
 *
 * Callers should fall back to whatever default they had pre-feature when
 * this returns null.
 */
export async function fetchDxReportOrthopulseStatus(
    patientId: string
): Promise<DxReportOrthopulseStatus | null> {
    const event = await fetchLatestEventByType(patientId, 'dx_report_submission');
    if (!event) return null;
    const formData = event.payload?.formData as Record<string, unknown> | undefined;
    const status = formData?.orthopulseStatus;
    if (status === 'mandatory' || status === 'recommended' || status === 'not_needed') {
        return status;
    }
    return null;
}
